import type {
  HumanStatementCall,
  ProblemStatementHumanReview,
  ProblemStatementHumanReviewPatch,
  ProblemStatementNarrative,
  ProblemStatementRating,
  ProblemStatementRollup,
  PsRuleHumanReview,
} from "@/services/api/case-checking";
import { applyRuleOverridesToRollup } from "@/components/case-checking/humanRuleReviewCascade";

export const RATING_TO_PILL: Record<ProblemStatementRating, HumanStatementCall | null> = {
  pass: "yes",
  fail: "no",
  unclear: "unclear",
  na: "na",
  informational: null,
};

export const PILL_LABEL: Record<HumanStatementCall, string> = {
  yes: "Yes",
  unclear: "Unclear",
  no: "No",
  na: "N/A",
};

export const RATING_DOT_CLASS: Record<ProblemStatementRating, string> = {
  pass: "bg-emerald-400",
  fail: "bg-rose-400",
  unclear: "bg-amber-400",
  na: "bg-slate-500",
  informational: "bg-sky-400",
};

export const RATING_TILE_CLASS: Record<ProblemStatementRating, string> = {
  pass: "bg-emerald-100 text-emerald-800 ring-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-200 dark:ring-emerald-500/30",
  fail: "bg-rose-100 text-rose-800 ring-rose-200 dark:bg-rose-500/15 dark:text-rose-200 dark:ring-rose-500/30",
  unclear: "bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-500/15 dark:text-amber-200 dark:ring-amber-500/30",
  na: "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-500/15 dark:text-slate-300 dark:ring-slate-500/30",
  informational: "bg-sky-100 text-sky-800 ring-sky-200 dark:bg-sky-500/15 dark:text-sky-200 dark:ring-sky-500/30",
};

export const PILL_ACTIVE_CLASS: Record<HumanStatementCall, string> = {
  yes: "bg-emerald-100 text-emerald-800 ring-emerald-300 dark:bg-emerald-500/15 dark:text-emerald-200 dark:ring-emerald-400/60",
  unclear: "bg-amber-100 text-amber-800 ring-amber-300 dark:bg-amber-500/15 dark:text-amber-200 dark:ring-amber-400/60",
  no: "bg-rose-100 text-rose-800 ring-rose-300 dark:bg-rose-500/15 dark:text-rose-200 dark:ring-rose-400/60",
  na: "bg-slate-100 text-slate-700 ring-slate-300 dark:bg-slate-500/15 dark:text-slate-200 dark:ring-slate-400/60",
};

export const PILL_DISPLAY_CLASS: Record<HumanStatementCall | "pending", string> = {
  yes: "bg-emerald-50 text-emerald-800 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-200 dark:ring-emerald-500/30",
  unclear: "bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-200 dark:ring-amber-500/30",
  no: "bg-rose-50 text-rose-800 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-200 dark:ring-rose-500/30",
  na: "bg-slate-100 text-slate-600 ring-slate-200 dark:bg-slate-500/10 dark:text-slate-300 dark:ring-slate-500/30",
  pending: "bg-slate-100 text-slate-500 ring-slate-200 dark:bg-slate-700/40 dark:text-slate-400 dark:ring-slate-600/40",
};

export function pillLabelForRating(rating: ProblemStatementRating): string {
  const pill = RATING_TO_PILL[rating];
  return pill ? PILL_LABEL[pill] : "—";
}

export type HumanSignoffStatus = "pending" | "agreed" | "overridden";

export function agentPillForRollup(rollup: ProblemStatementRollup): HumanStatementCall | null {
  return RATING_TO_PILL[rollup.rating];
}

export function humanSignoffStatus(
  rollup: ProblemStatementRollup,
  human?: ProblemStatementHumanReview | null,
): HumanSignoffStatus {
  if (!human?.reviewed_at) return "pending";
  const agent = agentPillForRollup(rollup);
  if (!agent) return "overridden";
  return human.call === agent ? "agreed" : "overridden";
}

const CALL_TO_RATING: Record<HumanStatementCall, ProblemStatementRating> = {
  yes: "pass",
  no: "fail",
  unclear: "unclear",
  na: "na",
};

/** Row dot / filter colour — human rule cascade, then statement sign-off, then agent. */
export function effectiveRowRating(
  rollup: ProblemStatementRollup,
  human?: ProblemStatementHumanReview | null,
  ruleOverrides?: Map<string, import("@/services/api/case-checking").PsRuleHumanReview>,
): ProblemStatementRating {
  if (ruleOverrides && ruleOverrides.size > 0) {
    const { rollup: cascaded, effectiveRules } = applyRuleOverridesToRollup(rollup, ruleOverrides);
    if (effectiveRules.some((r) => r.humanChanged)) {
      return cascaded.rating;
    }
  }
  const signoff = humanSignoffStatus(rollup, human);
  if (signoff !== "pending" && human?.call) {
    return CALL_TO_RATING[human.call];
  }
  return rollup.rating;
}

export function effectiveRowDotClass(
  rollup: ProblemStatementRollup,
  human?: ProblemStatementHumanReview | null,
  ruleOverrides?: Map<string, PsRuleHumanReview>,
): string {
  return RATING_DOT_CLASS[effectiveRowRating(rollup, human, ruleOverrides)];
}

export function emptyHumanReview(
  statementId: string,
  seed?: ProblemStatementHumanReview | null,
): ProblemStatementHumanReview {
  return {
    problem_statement_id: statementId,
    call: seed?.call ?? "unclear",
    notes: seed?.notes ?? "",
    reviewer_name: seed?.reviewer_name ?? "",
    reviewed_at: seed?.reviewed_at ?? null,
  };
}

export function clearHumanReviewPatch(statementId: string): ProblemStatementHumanReviewPatch {
  return {
    problem_statement_id: statementId,
    call: "unclear",
    notes: "",
    reviewer_name: "",
    reviewed_at: null,
    clear: true,
  };
}

/** Short line for summary list skim; falls back to first sentence of summary for older runs. */
export function problemStatementSkimText(narrative: ProblemStatementNarrative | undefined): string | null {
  const skim = narrative?.skim_answer?.trim();
  if (skim) return skim;
  const summary = narrative?.summary?.trim();
  if (!summary) return null;
  const firstSentence = summary.match(/^[^.!?]+[.!?]/);
  return (firstSentence ? firstSentence[0] : summary.slice(0, 120)).trim();
}
